import { supabase } from './supabase';
import type {
  FriendTag,
  FriendWithTags,
  FriendTagStats,
  FriendsByTagsResponse,
  AddFriendTagRequest,
  RemoveFriendTagRequest,
  UpdateFriendTagsRequest,
  GetFriendsByTagsRequest,
} from '../types/friends';

export class FriendTagsAPI {
  static async addFriendTag(request: AddFriendTagRequest): Promise<FriendTag> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data, error } = await supabase
      .from('friend_tags')
      .insert({
        user_id: user.id,
        friend_id: request.friend_id,
        tag_name: request.tag_name.trim(),
      })
      .select()
      .single();

    if (error) {
      console.error('添加朋友标签失败:', error);
      throw new Error(error.message);
    }

    return data;
  }

  static async removeFriendTag(request: RemoveFriendTagRequest): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { error } = await supabase
      .from('friend_tags')
      .delete()
      .match({
        user_id: user.id,
        friend_id: request.friend_id,
        tag_name: request.tag_name,
      });

    if (error) {
      console.error('移除朋友标签失败:', error);
      throw new Error(error.message);
    }
  }

  static async updateFriendTags(request: UpdateFriendTagsRequest): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { error: deleteError } = await supabase
      .from('friend_tags')
      .delete()
      .match({
        user_id: user.id,
        friend_id: request.friend_id,
      });

    if (deleteError) {
      console.error('删除旧标签失败:', deleteError);
      throw new Error(`删除旧标签失败: ${deleteError.message}`);
    }

    if (request.tags.length > 0) {
      const tagsData = request.tags.map((tag) => ({
        user_id: user.id,
        friend_id: request.friend_id,
        tag_name: tag.trim(),
      }));

      const { error: insertError } = await supabase
        .from('friend_tags')
        .insert(tagsData);

      if (insertError) {
        console.error('插入新标签失败:', insertError);
        throw new Error(`插入新标签失败: ${insertError.message}`);
      }
    }
  }

  static async getFriendsWithTags(): Promise<FriendWithTags[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data: friendsData, error: friendsError } = await supabase
      .from('friends')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (friendsError) {
      console.error('获取朋友列表失败:', friendsError);
      throw new Error(friendsError.message);
    }

    if (!friendsData || friendsData.length === 0) {
      return [];
    }

    const friendsWithTags: FriendWithTags[] = [];

    for (const friend of friendsData) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, username')
        .eq('id', friend.friend_id)
        .single();

      const { data: tagsData } = await supabase
        .from('friend_tags')
        .select('tag_name')
        .eq('user_id', user.id)
        .eq('friend_id', friend.friend_id);

      friendsWithTags.push({
        id: '',
        user_id: user.id,
        friend_id: friend.friend_id,
        status: 'active',
        created_at: '',
        updated_at: '',
        display_name: profileData?.display_name,
        avatar_url: profileData?.avatar_url,
        username: profileData?.username,
        tags: tagsData?.map((t) => t.tag_name) || [],
      });
    }

    return friendsWithTags;
  }

  static async getFriendsByTags(request: GetFriendsByTagsRequest): Promise<FriendsByTagsResponse[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data, error } = await supabase.rpc('get_friends_by_tags', {
      user_uuid: user.id,
      tag_names: request.tag_names,
    });

    if (error) {
      console.error('按标签筛选朋友失败:', error);
      throw new Error(error.message);
    }

    return data || [];
  }

  static async getFriendTags(friendId: string): Promise<string[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data, error } = await supabase
      .from('friend_tags')
      .select('tag_name')
      .match({
        user_id: user.id,
        friend_id: friendId,
      });

    if (error) {
      console.error('获取朋友标签失败:', error);
      throw new Error(error.message);
    }

    return data?.map((item) => item.tag_name) || [];
  }

  static async getPopularTags(): Promise<FriendTagStats[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data, error } = await supabase.rpc('get_popular_friend_tags', {
      user_uuid: user.id,
    });

    if (error) {
      console.error('获取热门标签失败:', error);
      throw new Error(error.message);
    }

    return data || [];
  }

  static async getAllUserTags(): Promise<string[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const { data, error } = await supabase
      .from('friend_tags')
      .select('tag_name')
      .eq('user_id', user.id)
      .order('tag_name');

    if (error) {
      console.error('获取用户标签失败:', error);
      throw new Error(error.message);
    }

    const tags = data?.map((item) => item.tag_name) || [];
    return Array.from(new Set(tags));
  }

  static async searchFriends(query: string): Promise<FriendWithTags[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('用户未登录');

    const allFriends = await this.getFriendsWithTags();

    const searchTerm = query.toLowerCase().trim();

    return allFriends.filter((friend) => {
      const nameMatch = friend.display_name?.toLowerCase().includes(searchTerm);
      const tagMatch = friend.tags.some((tag) => tag.toLowerCase().includes(searchTerm));
      return Boolean(nameMatch || tagMatch);
    });
  }

  static async getFriendsGroupedByTags(): Promise<{ [tagName: string]: FriendWithTags[] }> {
    const friends = await this.getFriendsWithTags();
    const grouped: { [tagName: string]: FriendWithTags[] } = {
      未分类: [],
    };

    friends.forEach((friend) => {
      if (friend.tags.length === 0) {
        grouped['未分类'].push(friend);
      } else {
        friend.tags.forEach((tag) => {
          if (!grouped[tag]) {
            grouped[tag] = [];
          }
          grouped[tag].push(friend);
        });
      }
    });

    return grouped;
  }

  static async inviteFriendsByTags(
    tagNames: string[],
    inviteData: { event_id: string; event_type: string; message?: string },
  ): Promise<{ success_count: number; failed_friends: string[] }> {
    const friends = await this.getFriendsByTags({ tag_names: tagNames });

    let success_count = 0;
    const failed_friends: string[] = [];

    for (const friend of friends) {
      try {
        success_count++;
      } catch (error) {
        console.error(`邀请朋友 ${friend.friend_display_name} 失败:`, error);
        failed_friends.push(friend.friend_display_name || friend.friend_id);
      }
    }

    return { success_count, failed_friends };
  }
}
